import { Component, h, Host, Prop, Element, Watch, Listen, Event } from '@stencil/core';
import { checkSlottedContentForInvalidHTML, generateRandomNumber, getSiblings } from '../../../../global/utils/utils';
/**
 * @slot sub-heading - Used for the inclusion of Phrasing content (see https://www.w3.org/TR/2012/WD-html-markup-20121025/common-models.html) eg. an anchor tag in the sub heading
 * @slot default - The content placed in this slot must be a agl-ds-radio-button-set.<br>The radio-button-group is to be used as a wrapper around a radio-button-set.<br>The radio-button-set then in turn houses the radio buttons.<dl><dt>A radio-button-set has built in layout behaviour to designate the following</dt><dt>2 radios results in each button taking up 50% each in 1 row</dt><dt>3 radios results in each button taking up 33% each in 1 row</dt><dt>4 radios results in each button taking up 50% each in 2 rows ( 2 in each)</dt><dt>5 radios results in each button taking up 33% each in 2 rows ( 3 and 2 in each)</dt><dt>6 radios results in each button taking up 33% each in 2 row  ( 3 each)</dt><dt>7 radios results in each button taking up 33% each in 3 row  ( 3, 3 and 1)</dt></dl> Image Radio buttons with a total button count not equal to 2 or 4 on mobile and desktop will have cropped images.
 */
export class RadioButtonGroupComponent {
  constructor() {
    this.isBasicRadioButton = false;
    this.isPanelRadioButton = false;
    /**
     *  Unique ID for the radio button group
     */
    this.radioButtonGroupId = generateRandomNumber();
    /**
     * The validation/ error text for the radio button group
     */
    this.validationText = null;
    /**
     * Shows the radio buttons in the group in an error state
     */
    this.hasError = false;
    /**
     * Determines the layout direction of the radio buttons on medium and large devices. This will override the default layout behavior of the radio buttons. Note single-column= false can't be used with the panel radio buttons.
     * @deprecated use layout
     */
    this.singleColumn = false;
    /**
     * Determines the no of columns the radio buttons will be allocated to on medium and large devices. This will override the default layout behavior of the radio buttons. Note noOfColumns can't be used with the panel radio buttons.
     */
    this.layout = 'default';
    /**
     * Determines the size of the heading (legend element). Note this will also automatically change the sub heading font to the correct value when the sub heading is present.
     */
    this.styledAs = 'title5';
  }
  dsCheckedChangeHandler(event) {
    // check to make sure that its the correct event that is being listened to as a child radio button group within a panel radio button will fire the parents event as well
    // if this check is not made then the parent panel radio button will close once a child radio button is selected
    if (event.composedPath()[0].groupName === this.groupName) {
      this.selectedValue = event.detail.value; //needs to be set for ngmodel and formcontrolname
      this.valueChange.emit(event.detail.value); // emit the value for the valuechange event
    }
  }
  selectedValueChanged() {
    this.init(this.hasError);
  }
  valueChanged(value) {
    if (this.value) {
      this.selectedValue = value;
    }
    this.init(this.hasError);
  }
  init(hasError) {
    // TODO look at splitting out this function as many parts do not need to be run on every watch change especially the layout function
    const radioButtonSets = getSiblings(this.host.querySelector('agl-ds-radio-button-set'));
    if ((radioButtonSets === null || radioButtonSets === void 0 ? void 0 : radioButtonSets.length) > 0) {
      for (let x = 0; x < radioButtonSets.length; x++) {
        const radioButtons = getSiblings(radioButtonSets[x].querySelector('agl-ds-radio-button'));
        if ((radioButtons === null || radioButtons === void 0 ? void 0 : radioButtons.length) > 0) {
          for (let y = 0; y < radioButtons.length; y++) {
            const radioButton = radioButtons[y];
            const radioButtonInput = radioButton.querySelectorAll('input')[0];
            //select the default radio button if required
            if (this.selectedValue && radioButtonInput) {
              radioButton.checked = radioButtonInput.value === this.selectedValue;
            }
            else {
              radioButton.checked = false;
            }
            let radioButtonBorderElement = null;
            radioButtonBorderElement = radioButton;
            this.isBasicRadioButton = radioButton.type === 'basic';
            this.isPanelRadioButton = radioButton.type === 'panel';
            if (!this.isPanelRadioButton) {
              radioButtonBorderElement = radioButton.querySelectorAll('label')[0];
            }
            else {
              this.layout = 'one-column';
            }
            if (radioButtonBorderElement && radioButtonInput) {
              if (hasError) {
                if (this.isBasicRadioButton || this.isPanelRadioButton) {
                  radioButtonInput.classList.add('basic-radio-button-error-state');
                }
                if (!this.isBasicRadioButton) {
                  radioButtonBorderElement.classList.add('error-state');
                }
                radioButtonInput.setAttribute('aria-describedby', this.radioButtonGroupId);
                radioButtonInput.setAttribute('aria-invalid', 'true');
              }
              else {
                if (this.isBasicRadioButton || this.isPanelRadioButton) {
                  radioButtonInput.classList.remove('basic-radio-button-error-state');
                }
                if (!this.isBasicRadioButton) {
                  radioButtonBorderElement.classList.remove('error-state');
                }
                radioButtonInput.removeAttribute('aria-describedby');
                radioButtonInput.removeAttribute('aria-invalid');
              }
            }
            this.layoutRadioButtons(radioButton, radioButtons.length, radioButtonInput);
          }
        }
      }
    }
  }
  layoutRadioButtons(radioButton, radioButtonCount, radioButtonInput) {
    //here to cater for the deprecated prop singleColumn
    if (this.singleColumn) {
      this.layout = 'one-column';
    }
    if (this.layout !== 'default' && radioButton.type !== 'panel') {
      if (this.layout === 'one-column') {
        radioButton.classList.add('col-sm-12');
      }
      else if (this.layout === 'two-column') {
        radioButton.classList.add('col-sm-12');
        radioButton.classList.add('col-md-6');
        radioButton.classList.add('remove-horizonal-padding');
      }
    }
    else {
      if (radioButton.type === 'basic') {
        //just sit the radio buttons side by side
        return;
      }
      else if (radioButton.type === 'panel') {
        radioButton.classList.add('col-sm-12');
      }
      else {
        radioButton.classList.add('remove-horizonal-padding');
        if (radioButton.size === 'small' && radioButtonCount === 2) {
          radioButton.classList.add('col-sm-12');
          radioButton.classList.add('col-md-5');
        }
        else {
          if (radioButtonCount === 2 || radioButtonCount === 4) {
            radioButton.classList.add('col-sm-12');
            radioButton.classList.add('col-md-6');
            if (radioButtonInput && radioButton.type === 'image') {
              radioButtonInput.classList.remove('icon-or-image-radio__cropped-image');
            }
          }
          else {
            radioButton.classList.add('col-sm-12');
            radioButton.classList.add('col-md-4');
            if (radioButtonInput && radioButton.type === 'image') {
              radioButtonInput.classList.add('icon-or-image-radio__cropped-image');
            }
          }
        }
      }
    }
  }
  componentDidLoad() {
    this.init(this.hasError);
  }
  componentWillLoad() {
    checkSlottedContentForInvalidHTML(this.host, ['agl-ds-radio-button-group', 'agl-ds-radio-button-set', 'span']);
    this.valueChanged(this.value);
  }
  renderSubHeading() {
    var _a, _b;
    if (((_a = this.subHeading) === null || _a === void 0 ? void 0 : _a.length) > 0 || ((_b = this.host.querySelector('[slot="sub-heading"]')) === null || _b === void 0 ? void 0 : _b.innerHTML.length) > 0) {
      return h("span", { slot: "sub-heading" }, this.subHeading ? this.subHeading : h("slot", { name: "sub-heading" }));
    }
    return '';
  }
  render() {
    return (h(Host, null,
      h("agl-ds-form-field-label", { heading: this.heading, styledAs: this.styledAs },
        this.renderSubHeading(),
        h("slot", null)),
      this.validationText && (h("div", null,
        h("agl-ds-hint-validation-message", { class: { ['default-radio-button-validation-message']: !this.isPanelRadioButton && !this.isBasicRadioButton }, asDescribedbyId: this.radioButtonGroupId, "has-error": this.hasError },
          h("div", { slot: "validation-text" }, this.validationText))))));
  }
  static get is() { return "agl-ds-radio-button-group"; }
  static get encapsulation() { return "scoped"; }
  static get originalStyleUrls() { return {
    "$": ["radio-button-group.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["radio-button-group.component.css"]
  }; }
  static get properties() { return {
    "radioButtonGroupId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Unique ID for the radio button group"
      },
      "attribute": "radio-button-group-id",
      "reflect": false,
      "defaultValue": "generateRandomNumber()"
    },
    "groupName": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": true,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The group name for the radio button"
      },
      "attribute": "group-name",
      "reflect": false
    },
    "heading": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The legend/heading for the radio button input field"
      },
      "attribute": "heading",
      "reflect": false
    },
    "subHeading": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The sub heading for the radio button input field. There is also a slot which can be used for the inclusion of Phrasing content\n(see https://www.w3.org/TR/2012/WD-html-markup-20121025/common-models.html#common.elem.phrasing) eg. an anchor tag in the sub heading"
      },
      "attribute": "sub-heading",
      "reflect": false
    },
    "validationText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The validation/ error text for the radio button group"
      },
      "attribute": "validation-text",
      "reflect": false,
      "defaultValue": "null"
    },
    "hasError": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Shows the radio buttons in the group in an error state"
      },
      "attribute": "has-error",
      "reflect": false,
      "defaultValue": "false"
    },
    "selectedValue": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the default value of the radio button group"
      },
      "attribute": "selected-value",
      "reflect": false
    },
    "value": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [{
            "text": "use selectedValue",
            "name": "deprecated"
          }],
        "text": "Sets the default value of the radio button group"
      },
      "attribute": "value",
      "reflect": false
    },
    "singleColumn": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [{
            "text": "use layout",
            "name": "deprecated"
          }],
        "text": "Determines the layout direction of the radio buttons on medium and large devices. This will override the default layout behavior of the radio buttons. Note single-column= false can't be used with the panel radio buttons."
      },
      "attribute": "single-column",
      "reflect": false,
      "defaultValue": "false"
    },
    "layout": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "Layout",
        "resolved": "\"default\" | \"one-column\" | \"two-column\"",
        "references": {
          "Layout": {
            "location": "import",
            "path": "../common/radio-button.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines the no of columns the radio buttons will be allocated to on medium and large devices. This will override the default layout behavior of the radio buttons. Note noOfColumns can't be used with the panel radio buttons."
      },
      "attribute": "layout",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "styledAs": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "FormFieldTitleType",
        "resolved": "\"title3\" | \"title5\"",
        "references": {
          "FormFieldTitleType": {
            "location": "import",
            "path": "../../form-field-label/form-field-label.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Determines the size of the heading (legend element). Note this will also automatically change the sub heading font to the correct value when the sub heading is present."
      },
      "attribute": "styled-as",
      "reflect": false,
      "defaultValue": "'title5'"
    }
  }; }
  static get events() { return [{
      "method": "valueChange",
      "name": "valueChange",
      "bubbles": false,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Radio button value changed event"
      },
      "complexType": {
        "original": "any",
        "resolved": "any",
        "references": {}
      }
    }]; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "selectedValue",
      "methodName": "selectedValueChanged"
    }, {
      "propName": "value",
      "methodName": "valueChanged"
    }, {
      "propName": "hasError",
      "methodName": "init"
    }]; }
  static get listeners() { return [{
      "name": "dsCheckedChange",
      "method": "dsCheckedChangeHandler",
      "target": undefined,
      "capture": false,
      "passive": false
    }]; }
}
